<!-- 用户管理页面：admin 仅管理 operator；superadmin 管理全部 -->
<template>
  <div class="users-page" v-loading="loading">
    <div class="toolbar">
      <el-button type="primary" @click="openCreate">新增用户</el-button>
    </div>
    <el-table :data="users" style="width: 100%">
      <el-table-column prop="username" label="用户名" width="180" />
      <el-table-column prop="display_name" label="显示名称" width="180" />
      <el-table-column prop="role" label="角色" width="140" />
      <el-table-column
        v-if="isSuperAdmin"
        prop="tenant_name"
        label="租户"
        min-width="180"
        sortable
      />
      <el-table-column
        v-if="isSuperAdmin"
        prop="tenant_subname"
        label="子域名"
        min-width="160"
        sortable
      />
      <el-table-column prop="is_active" label="状态" width="120">
        <template #default="scope">
          <el-tag :type="scope.row.is_active ? 'success' : 'info'">{{
            scope.row.is_active ? "启用" : "禁用"
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间">
        <template #default="scope">
          {{ formatDate(scope.row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150">
        <template #default="scope">
          <el-button
            type="danger"
            size="small"
            @click="onDelete(scope.row)"
            v-if="canDelete(scope.row)"
            >删除</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="createVisible" title="新增用户" width="480px">
      <el-form :model="createForm" label-width="100px">
        <el-form-item label="用户名"
          ><el-input v-model="createForm.username"
        /></el-form-item>
        <el-form-item label="显示名称"
          ><el-input v-model="createForm.display_name"
        /></el-form-item>
        <el-form-item label="密码"
          ><el-input
            v-model="createForm.password"
            type="password"
            show-password
        /></el-form-item>
        <el-form-item label="角色">
          <el-select v-model="createForm.role" style="width: 100%">
            <el-option label="操作员" value="operator" />
            <el-option label="管理员" value="admin" v-if="isSuperAdmin" />
            <el-option
              label="超级管理员"
              value="super_admin"
              v-if="isSuperAdmin"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="租户" v-if="showTenantSelect">
          <el-select
            v-model="createForm.tenant_id"
            style="width: 100%"
            placeholder="请选择租户"
          >
            <el-option
              v-for="tenant in tenants"
              :key="tenant.id"
              :label="tenant.name"
              :value="tenant.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" @click="createUser">创建</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { UserServiceApi, TenantServiceApi } from "@/services/dataService";
import { useStore } from "vuex";

const store = useStore();
const role = computed(() => store.getters.getUserRole);
const isSuperAdmin = computed(() => role.value === "super_admin");
const isAdmin = computed(() => role.value === "admin");
const tenants = ref<{ id: string | number; name: string }[]>([]);

interface UiUserItem {
  id: number;
  username: string;
  display_name?: string;
  role: string;
  is_active?: boolean;
  created_at?: string;
  tenant_id?: number | string;
  tenant_name?: string;
  tenant_subname?: string;
}

const users = ref<UiUserItem[]>([]);
const loading = ref(false);
const createVisible = ref(false);
const createForm = reactive({
  username: "",
  display_name: "",
  password: "",
  role: "operator",
  tenant_id: undefined as string | number | undefined,
});

const showTenantSelect = computed(
  () => isSuperAdmin.value && createForm.role !== "super_admin"
);

const fetchUsers = async () => {
  loading.value = true;
  try {
    const resp = await UserServiceApi.listUsers({});
    const payload = (resp as { data?: { data?: { items?: UiUserItem[] } } })
      .data?.data;
    users.value = (payload?.items as UiUserItem[]) || [];
  } finally {
    loading.value = false;
  }
};

const fetchTenants = async () => {
  try {
    const resp = await TenantServiceApi.listTenants({ page_size: 100 });
    const payload = (
      resp as {
        data?: { data?: { items?: { id: string | number; name: string }[] } };
      }
    )?.data?.data;
    tenants.value =
      (payload?.items as { id: string | number; name: string }[]) || [];
  } catch (err) {
    console.error("加载租户列表失败", err);
  }
};

const openCreate = () => {
  createForm.username = "";
  createForm.display_name = "";
  createForm.password = "";
  createForm.role = isSuperAdmin.value ? "operator" : "operator"; // admin 仅 operator
  createForm.tenant_id = undefined;
  if (isSuperAdmin.value) {
    fetchTenants();
  }
  createVisible.value = true;
};

const createUser = async () => {
  if (!createForm.username || !createForm.password) {
    ElMessage.warning("请输入用户名与密码");
    return;
  }
  if (showTenantSelect.value && !createForm.tenant_id) {
    ElMessage.warning("请选择租户");
    return;
  }
  const resp = await UserServiceApi.createUser({
    ...createForm,
    is_active: true,
  });
  const ok =
    (resp as { data?: { code?: number; message?: string } }).data?.code === 0;
  if (ok) {
    ElMessage.success("用户创建成功");
    createVisible.value = false;
    fetchUsers();
  } else {
    const msg =
      (resp as { data?: { message?: string } }).data?.message || "创建失败";
    ElMessage.error(msg);
  }
};

const canDelete = (row: UiUserItem) => {
  if (isSuperAdmin.value) return true;
  if (isAdmin.value && row.role === "operator") return true;
  return false;
};

const onDelete = (row: UiUserItem) => {
  ElMessageBox.confirm(
    `确认删除用户 ${row.username} ？此操作不可撤销。`,
    "确认删除",
    {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    }
  )
    .then(async () => {
      const resp = await UserServiceApi.deleteUser(row.id);
      const rs = resp as {
        data?: { code?: number; message?: string };
        status?: number;
      };
      const ok = rs.data?.code === 0 || rs.status === 204;
      if (ok) {
        ElMessage.success("用户已删除");
        fetchUsers();
      } else {
        const msg =
          (resp as { data?: { message?: string } }).data?.message || "删除失败";
        ElMessage.error(msg);
      }
    })
    .catch(() => {
      // 用户取消
    });
};

const formatDate = (d: string) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

onMounted(fetchUsers);
</script>

<style scoped>
.users-page {
  padding: 16px;
}
.toolbar {
  margin-bottom: 12px;
}
</style>
